import React from "react";
import { Send, Lock, Instagram, Mail, ExternalLink, ShoppingCart, Megaphone, Plane, MapPin } from "lucide-react";
import { InstallBanner, InstallAppPillButton } from "./InstallApp";
import IpfsImage from "./IpfsImage";
import { LegalDisclaimer } from "./LegalDisclaimer";

const BRAND_LOGO_CID = process.env.REACT_APP_BRAND_LOGO_CID || "bafkreifec5caqgaskmdnbkkcbuscnrodmqqbnq34rw22cxfw3fkl67wiru";

const getIconForLink = (label = "", url = "") => {
    const s = `${label} ${url}`.toLowerCase();
    if (s.includes("instagram")) return Instagram;
    if (s.includes("signal")) return Lock;
    if (s.includes("t.me") || s.includes("telegram")) return Send;
    if (s.includes("mailto") || (s.includes("@") && s.includes("."))) return Mail;
    return ExternalLink;
};

// Reusable neon pill stat (info badges under the title)
const StatPill = ({ children, accent = "text-[#e879f9]" }) => (
    <div
        className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full border border-[#7c3aed]/60 bg-[#1a0b2e]/60 shadow-[inset_0_0_18px_rgba(124,58,237,0.25),0_0_18px_rgba(124,58,237,0.35)]"
    >
        <span className={`font-mono uppercase tracking-[0.18em] text-xs sm:text-sm font-semibold ${accent}`}>
            {children}
        </span>
    </div>
);

// Reusable dark contact card (2-col grid). Uses custom icon_url if provided, otherwise auto.
const ContactCard = ({ link }) => {
    const Icon = getIconForLink(link.label, link.url);
    const hasCustomIcon = Boolean(link.icon_url);
    return (
        <a
            href={link.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`splash-link-${link.id || link.label}`}
            className="group flex items-center gap-3 rounded-2xl bg-[#150826]/85 border border-[#7c3aed]/40 px-4 py-3.5 hover:bg-[#1f0f36] hover:border-[#e879f9] hover:shadow-[0_0_18px_rgba(232,121,249,0.4)] active:scale-[0.98] transition-all"
        >
            <div className="w-9 h-9 rounded-full bg-[#7c3aed]/15 border border-[#7c3aed]/50 flex items-center justify-center shrink-0 overflow-hidden">
                {hasCustomIcon ? (
                    <IpfsImage
                        src={link.icon_url}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                        fallback={<Icon className="w-4 h-4 text-[#e879f9]" />}
                    />
                ) : (
                    <Icon className="w-4 h-4 text-[#e879f9]" />
                )}
            </div>
            <span className="flex-1 min-w-0 font-mono uppercase tracking-wide text-xs sm:text-[13px] font-semibold text-white break-words leading-tight">
                {link.label}
            </span>
            <span className="w-7 h-7 rounded-full bg-[#7c3aed]/20 border border-[#7c3aed]/60 flex items-center justify-center shrink-0 group-hover:bg-[#e879f9]/25 transition-colors">
                <span className="text-[#e879f9] text-sm leading-none">→</span>
            </span>
        </a>
    );
};

// Section heading (e.g. "UNISCITI ALLA COMMUNITY")
const SectionHeading = ({ children }) => (
    <div className="w-full text-center mb-4 mt-2">
        <span className="font-mono uppercase tracking-[0.32em] text-[11px] sm:text-xs text-[#a78bfa]/80">
            {children}
        </span>
    </div>
);

// Linkify @handles and URLs inside free-form description text.
// - @something → <a href="https://t.me/something"> styled chip
// - https://... or http://... → <a> link with neon underline
// Preserves line-breaks via white-space rule on parent.
const LINKIFY_REGEX = /(@[a-zA-Z0-9_]{3,32})|((?:https?:\/\/)[^\s]+)/g;

const renderLinkified = (text) => {
    if (!text) return null;
    const parts = [];
    let lastIdx = 0;
    let match;
    let key = 0;
    LINKIFY_REGEX.lastIndex = 0;
    while ((match = LINKIFY_REGEX.exec(text)) !== null) {
        if (match.index > lastIdx) {
            parts.push(text.slice(lastIdx, match.index));
        }
        if (match[1]) {
            // @handle
            const handle = match[1].slice(1);
            parts.push(
                <a
                    key={`h-${key++}`}
                    href={`https://t.me/${handle}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center px-2 py-0.5 mx-0.5 rounded-full bg-[#7c3aed]/20 border border-[#7c3aed]/50 text-[#e879f9] hover:bg-[#e879f9]/20 hover:border-[#e879f9] hover:text-white transition-colors font-mono text-xs font-semibold"
                >
                    @{handle}
                </a>
            );
        } else if (match[2]) {
            // URL
            parts.push(
                <a
                    key={`u-${key++}`}
                    href={match[2]}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#e879f9] underline decoration-dotted underline-offset-2 hover:text-[#f0abfc] transition-colors break-all"
                >
                    {match[2]}
                </a>
            );
        }
        lastIdx = LINKIFY_REGEX.lastIndex;
    }
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));
    return parts;
};

const SplashHome = ({ open, onClose, info, badges, reviews, telegramHandle = "il_vero_pinko" }) => {
    if (!open) return null;
    const links = info?.links || [];
    const description = info?.description || "";

    // Read cached badges synchronously so we render the right values on first paint.
    let cachedBadges = null;
    try {
        const raw = localStorage.getItem("splash_badges_cache");
        if (raw) {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr)) cachedBadges = arr;
        }
    } catch (_) { /* ignore */ }

    // Prefer fresh from props, then cache, then nothing.
    let safeBadges = null;
    if (Array.isArray(badges) && badges.length > 0) {
        safeBadges = badges.slice(0, 3).filter((b) => b && b.trim());
        // Persist for next visit so we never flash defaults again
        try { localStorage.setItem("splash_badges_cache", JSON.stringify(safeBadges)); } catch (_) { /* ignore */ }
    } else if (cachedBadges && cachedBadges.length > 0) {
        safeBadges = cachedBadges.slice(0, 3).filter((b) => b && b.trim());
    }
    const hasBadges = Array.isArray(safeBadges) && safeBadges.length > 0;

    return (
        <div
            className="fixed inset-0 z-[100] overflow-y-auto"
            style={{
                background:
                    "radial-gradient(ellipse 100% 70% at 50% 0%, rgba(124,58,237,0.25), transparent 60%), radial-gradient(ellipse 90% 60% at 50% 100%, rgba(232,121,249,0.15), transparent 60%), linear-gradient(180deg, #07020f 0%, #0d0418 40%, #0a0314 100%)",
            }}
            data-testid="splash-home"
        >
            {/* Top install banner */}
            <InstallBanner />

            {/* Subtle vertical light shafts */}
            <div
                className="pointer-events-none absolute inset-0 opacity-30"
                style={{
                    background:
                        "radial-gradient(ellipse 30% 80% at 50% 50%, rgba(167,139,250,0.18), transparent 60%)",
                }}
            />

            <div className="relative max-w-md mx-auto px-5 pt-10 pb-14 flex flex-col items-center">
                {/* Brand logo (circular framed) */}
                <div className="relative w-36 h-36 sm:w-40 sm:h-40 mb-5">
                    {/* Soft outer glow */}
                    <div className="absolute -inset-4 rounded-full bg-[#7c3aed]/35 blur-2xl" />
                    {/* Spinning neon ring */}
                    <div
                        className="absolute -inset-1 rounded-full animate-spin-slow"
                        style={{
                            background:
                                "conic-gradient(from 0deg, #e879f9, #a78bfa, #7c3aed, #5b21b6, #e879f9)",
                            filter: "blur(2px)",
                            opacity: 0.85,
                        }}
                    />
                    {/* Inner dark ring */}
                    <div className="absolute inset-0 rounded-full bg-[#0d0418] border-2 border-[#e879f9]/70 shadow-[0_0_28px_rgba(232,121,249,0.5)]" />
                    {/* Logo image (full image, properly fitted) — uses IpfsImage with gateway fallback */}
                    <div
                        data-testid="splash-brand-logo"
                        className="absolute inset-[8px] rounded-full overflow-hidden select-none pointer-events-none"
                    >
                        <IpfsImage
                            src={`https://nomelupin.mypinata.cloud/ipfs/${BRAND_LOGO_CID}`}
                            alt="il vero pinko"
                            loading="eager"
                            fetchPriority="high"
                            decoding="async"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>

                {/* Name with neon gradient (purple → white → magenta) */}
                <h1
                    className="text-center font-display text-5xl sm:text-6xl leading-[0.95] tracking-tight mb-3 select-none"
                    data-testid="splash-handle"
                >
                    <span
                        className="bg-clip-text text-transparent drop-shadow-[0_0_24px_rgba(232,121,249,0.5)]"
                        style={{
                            backgroundImage:
                                "linear-gradient(90deg, #a78bfa 0%, #ffffff 50%, #e879f9 100%)",
                        }}
                    >
                        IL VERO PINKO
                    </span>
                </h1>

                {/* Featured: Pinko Listino channel — compact, right under the title */}
                <a
                    href="https://t.me/pinko_listino"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="featured-listino"
                    className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full bg-gradient-to-r from-[#5b21b6] via-[#7c3aed] to-[#e879f9] hover:from-[#e879f9] hover:via-[#a78bfa] hover:to-[#5b21b6] shadow-[0_2px_14px_rgba(232,121,249,0.5)] hover:shadow-[0_4px_22px_rgba(232,121,249,0.8)] active:scale-95 transition-all"
                >
                    <Megaphone className="w-3.5 h-3.5 text-white drop-shadow shrink-0" />
                    <span className="font-mono uppercase tracking-[0.18em] text-[11px] font-bold text-white drop-shadow">
                        Canale Ufficiale
                    </span>
                </a>

                {/* Legal disclaimer — prominent right after brand identity */}
                <div className="w-full mb-4">
                    <LegalDisclaimer variant="strong" />
                </div>

                {/* Stat pills (only render once we know what to show — avoids flashing defaults) */}
                {hasBadges && (
                    <div className="flex flex-col items-center gap-2 mb-5 w-full" data-testid="splash-badges">
                        {safeBadges.map((b, i) => (
                            <StatPill key={i}>{b}</StatPill>
                        ))}
                    </div>
                )}

                {/* Description hidden by user request — re-enable here if needed in the future */}

                {/* Primary CTA: Menu Catalogo */}
                <button
                    onClick={onClose}
                    data-testid="splash-menu-cta"
                    className="w-full mb-4 inline-flex items-center justify-center gap-3 py-4 rounded-full bg-gradient-to-b from-[#1a0b2e] to-[#0d0418] border-2 border-[#7c3aed] shadow-[0_0_28px_rgba(124,58,237,0.55),inset_0_0_18px_rgba(124,58,237,0.2)] hover:shadow-[0_0_38px_rgba(232,121,249,0.7)] hover:border-[#e879f9] active:scale-[0.98] transition-all"
                >
                    <ShoppingCart className="w-5 h-5 text-[#e879f9]" />
                    <span className="font-mono uppercase tracking-[0.18em] text-base sm:text-lg font-semibold text-[#e879f9] drop-shadow-[0_0_10px_rgba(232,121,249,0.6)]">
                        Menu Catalogo
                    </span>
                </button>

                {/* Order CTAs — Italia / Estero (compact 2-col) */}
                <div className="w-full grid grid-cols-2 gap-2 mb-6" data-testid="order-ctas">
                    <a
                        href="https://t.me/ilveropinketto"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="order-italia"
                        className="group flex flex-col items-center justify-center gap-0.5 px-2 py-2 rounded-2xl bg-[#150826]/85 border border-[#7c3aed]/55 hover:border-[#e879f9] hover:bg-[#1f0f36] hover:shadow-[0_0_14px_rgba(232,121,249,0.4)] active:scale-95 transition-all"
                    >
                        <span className="font-mono uppercase tracking-[0.14em] text-[9px] font-bold text-white/55 flex items-center gap-1">
                            🇮🇹 Italia
                        </span>
                        <span className="font-mono text-[11px] font-bold text-[#e879f9] truncate max-w-full">
                            @ilveropinketto
                        </span>
                    </a>
                    <a
                        href="https://t.me/ilveropinko"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="order-estero"
                        className="group flex flex-col items-center justify-center gap-0.5 px-2 py-2 rounded-2xl bg-[#150826]/85 border border-[#7c3aed]/55 hover:border-[#e879f9] hover:bg-[#1f0f36] hover:shadow-[0_0_14px_rgba(232,121,249,0.4)] active:scale-95 transition-all"
                    >
                        <span className="font-mono uppercase tracking-[0.14em] text-[9px] font-bold text-white/55 flex items-center gap-1">
                            🌍 Estero
                        </span>
                        <span className="font-mono text-[11px] font-bold text-[#e879f9] truncate max-w-full">
                            @ilveropinko
                        </span>
                    </a>
                </div>

                {/* Section: Community */}
                {links.length > 0 && (
                    <>
                        <SectionHeading>Uniscit­i alla community</SectionHeading>
                        <div className="grid grid-cols-2 gap-2.5 w-full" data-testid="splash-links-grid">
                            {links.map((l) => (
                                <ContactCard key={l.id || l.label} link={l} />
                            ))}
                        </div>
                    </>
                )}

                {/* Reviews strip — horizontal scrollable photos */}
                {Array.isArray(reviews) && reviews.length > 0 && (
                    <div className="w-full mt-8" data-testid="splash-reviews">
                        <SectionHeading>Recensioni</SectionHeading>
                        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory -mx-2 px-2 pb-1 no-scrollbar">
                            {reviews.map((r) => (
                                <a
                                    key={r.id}
                                    href={r.photo_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`review-${r.id}`}
                                    className="snap-start shrink-0 w-40 sm:w-48 rounded-2xl overflow-hidden border border-[#7c3aed]/45 hover:border-[#e879f9] bg-[#0d0418] shadow-[0_2px_16px_rgba(124,58,237,0.35)] hover:shadow-[0_4px_22px_rgba(232,121,249,0.55)] active:scale-[0.98] transition-all"
                                >
                                    <div className="aspect-[3/4] bg-[#150826]">
                                        <IpfsImage
                                            src={r.photo_url}
                                            alt={r.caption || "Recensione"}
                                            loading="lazy"
                                            decoding="async"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    {r.caption && (
                                        <div className="px-2 py-1.5 border-t border-[#7c3aed]/25 bg-[#150826]/80 text-center">
                                            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#e879f9] line-clamp-2">
                                                {r.caption}
                                            </span>
                                        </div>
                                    )}
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="mt-10 text-center flex flex-col items-center gap-3">
                    <button
                        onClick={onClose}
                        data-testid="splash-skip"
                        className="text-xs font-mono text-white/50 hover:text-[#e879f9] underline transition-colors"
                    >
                        vai al menu
                    </button>

                    {/* Install app pill (discrete) */}
                    <InstallAppPillButton />

                    <p className="mt-2 text-[10px] font-mono tracking-widest text-white/30 flex items-center gap-1.5">
                        creato da
                        <a
                            href="https://t.me/ilverolupin"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="credit-ilverolupin"
                            className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#7c3aed]/15 border border-[#7c3aed]/50 text-[#e879f9] hover:bg-[#e879f9]/15 hover:border-[#e879f9] hover:text-white transition-colors normal-case tracking-normal"
                            title="Vuoi un sito simile? Scrivimi"
                        >
                            @ilverolupin
                        </a>
                    </p>

                    <a
                        href="https://ilverolupin.eth.limo/#/p/men-bot-web-app-sito-web-con-dominio-eth-216c795c"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="cta-site-creation"
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#e879f9] hover:from-[#e879f9] hover:to-[#7c3aed] text-white shadow-[0_2px_12px_rgba(232,121,249,0.45)] hover:shadow-[0_4px_22px_rgba(232,121,249,0.8)] active:scale-95 transition-all"
                    >
                        <ExternalLink className="w-3 h-3" />
                        <span className="font-mono uppercase tracking-[0.18em] text-[10px] font-bold">
                            Per creazione sito
                        </span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default SplashHome;
